import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useModalToggle } from '@make-software/tg-mini-app-ui';

import {
  makeSubnamePrimary,
  AppDispatch, useMe, fetchGiveawayData, setError
} from '@/shared';
import { TxStatus } from '@/entities';
import {
  buildRedirectLinkToCWM,
  getDevicePlatform,
  getEncodedAuthData,
  openLink,
  shouldBeRedirectedToCWM,
  getErrorMessage,
} from '@/utils';
import { useAuthData } from '@/features';

export const useMakeSubnamePrimary = (subname: string) => {
  const dispatch = useDispatch<AppDispatch>();
  const { initData } = useAuthData();

  const meService = useMe();
  const me = meService.data;
  const senderPK = me?.wallet?.public_key ?? null;

  const { open: isMakePrimaryModalOpen, setOpen: setIsMakePrimaryModalOpen } = useModalToggle('make-primary-modal');
  const [makePrimaryStatus, setMakePrimaryStatus] = useState<TxStatus>('error');
  const [submitting, setSubmitting] = useState(false);

  const onMakeSubnamePrimary = useCallback(async () => {
    if (!subname) return;

    if (shouldBeRedirectedToCWM()) {
      const platform = getDevicePlatform();
      const encodedData = getEncodedAuthData(initData || '');
      const redirectLink = buildRedirectLinkToCWM(platform, encodedData);
      openLink(redirectLink)();
    }

    const mapStatus: Record<string, TxStatus> = {
      ping: 'processing',
      sent: 'processing',
      processed: 'processed'
    };

    const onStatusUpdate = (status: string) => {
      const txModalStatus: TxStatus = mapStatus[status] ?? 'error';

      if (makePrimaryStatus !== txModalStatus) {
        setMakePrimaryStatus(txModalStatus);
      }

      if (
        !isMakePrimaryModalOpen &&
        status !== 'cancelled' &&
        makePrimaryStatus !== txModalStatus
      ) {
        setIsMakePrimaryModalOpen(true);
        setMakePrimaryStatus(txModalStatus);
      }

      if (txModalStatus === 'processed') {
        setMakePrimaryStatus(txModalStatus);
        meService.refresh();
        dispatch(fetchGiveawayData());
      }
    };

    setSubmitting(true);

    try {
      const resp = await makeSubnamePrimary(subname);
      const csprclick = (window as any).csprclick;
      if (!csprclick?.send) throw new Error('Wallet bridge is unavailable');

      const res = await csprclick.send(
        JSON.stringify(resp.data?.transaction),
        senderPK!,
        onStatusUpdate
      );

      if (res?.cancelled) {
        dispatch(setError('Transaction was cancelled. Please try again.'));
      } else if (res?.error) {
        const error = String(
          res.error +
          ' data:' +
          JSON.stringify(res.errorData || 'no error data') +
          ' senderPK: ' +
          senderPK || 'Something went wrong. Please try again.'
        );
        dispatch(setError(error));
        setMakePrimaryStatus('error');
      }
    } catch (err: unknown) {
      const actacc = (window as any).csprclick?.getActiveAccount();
      const st = actacc ? JSON.stringify(actacc) : 'no active account';
      const error =
        getErrorMessage(err, 'Something went wrong. Please try again.') + ` senderPK: ${st}`;
      dispatch(setError(error));
      setMakePrimaryStatus('error');
    } finally {
      setSubmitting(false);
      meService.refresh();
      dispatch(fetchGiveawayData());
    }
  }, [dispatch, initData, isMakePrimaryModalOpen, makePrimaryStatus, senderPK, subname]);

  return {
    isMakePrimaryModalOpen,
    setIsMakePrimaryModalOpen,
    makePrimaryStatus,
    submitting,
    onMakeSubnamePrimary
  };
};
